
import React, { useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Button,
  Typography,
  Paper,
} from "@mui/material";

import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";

// FIX: Use the React web-specific animation package
import { useSpring, animated } from "@react-spring/web";

const SignUp = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");

  // Chat box slide-up and fade animation
  const animation = useSpring({
    transform: isOpen ? "translateY(0%)" : "translateY(100%)",
    opacity: isOpen ? 1 : 0,
    config: {
      tension: 280,
      friction: 25,
    },
  });

  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSend = () => {
    const trimmedInput = input.trim();

    if (!trimmedInput) return;

    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${prev.length}`,
        text: trimmedInput,
        sender: "user",
      },
    ]);

    setInput("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 1000,
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* Floating chat launcher */}
      {!isOpen && (
        <IconButton
          aria-label="Open chat"
          onClick={toggleChat}
          sx={{
            width: 56,
            height: 56,
            color: "#ffffff",
            backgroundColor: "#1976d2",
            boxShadow: "0 5px 18px rgba(25, 118, 210, 0.35)",
            "&:hover": {
              backgroundColor: "#115293",
              transform: "translateY(-2px)",
            },
            transition: "all 0.2s ease",
          }}
        >
          <ChatBubbleOutlineIcon />
        </IconButton>
      )}

      {/* Animated chat window */}
      {isOpen && (
        <animated.div
          style={{
            ...animation,
            transformOrigin: "bottom right",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              width: {
                xs: "calc(100vw - 32px)",
                sm: 340,
              },
              maxWidth: 380,
              height: 440,
              maxHeight: "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 3,
              border: "1px solid #e0e7ef",
              backgroundColor: "#ffffff",
              boxShadow: "0 12px 40px rgba(15, 35, 60, 0.2)",
            }}
          >
            {/* Chat header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                color: "#ffffff",
                background: "linear-gradient(135deg, #063A6B, #1976d2)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.16)",
                  }}
                >
                  <ChatBubbleOutlineIcon />
                </Box>

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, lineHeight: 1.4 }}
                  >
                    Chat Support
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    How can we help you?
                  </Typography>
                </Box>
              </Box>

              <IconButton
                aria-label="Close chat"
                onClick={toggleChat}
                size="small"
                sx={{
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.15)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Messages area */}
            <Box
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.2,
                p: 2,
                backgroundColor: "#f5f8fc",
              }}
            >
              {messages.length === 0 ? (
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 58,
                      height: 58,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      backgroundColor: "#e3efff",
                      color: "#1976d2",
                      mb: 1.5,
                    }}
                  >
                    <ChatBubbleOutlineIcon sx={{ fontSize: 30 }} />
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: "#243447" }}
                  >
                    Welcome!
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7785", mt: 0.5 }}
                  >
                    Send us a message to start the conversation.
                  </Typography>
                </Box>
              ) : (
                messages.map((message) => {
                  const isUser = message.sender === "user";

                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: "flex",
                        justifyContent: isUser
                          ? "flex-end"
                          : "flex-start",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "85%",
                          px: 1.5,
                          py: 1,
                          borderRadius: isUser
                            ? "14px 14px 3px 14px"
                            : "14px 14px 14px 3px",
                          backgroundColor: isUser
                            ? "#1976d2"
                            : "#ffffff",
                          color: isUser ? "#ffffff" : "#243447",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.06)",
                          overflowWrap: "anywhere",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {message.text}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Message input */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                borderTop: "1px solid #e7edf4",
                backgroundColor: "#ffffff",
              }}
            >
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                multiline
                maxRows={3}
                placeholder="Type a message..."
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                inputProps={{
                  "aria-label": "Type a message",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    fontSize: 14,
                    "& fieldset": {
                      borderColor: "#dce4ed",
                    },
                    "&:hover fieldset": {
                      borderColor: "#90b8e8",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />

              <Button
                variant="contained"
                onClick={handleSend}
                disabled={!input.trim()}
                aria-label="Send message"
                sx={{
                  minWidth: 48,
                  width: 48,
                  height: 42,
                  borderRadius: 2,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "0 3px 10px rgba(25,118,210,0.25)",
                  },
                }}
              >
                <SendIcon fontSize="small" />
              </Button>
            </Box>
          </Paper>
        </animated.div>
      )}
    </Box>
  );
};

export default SignUp;
